import React, { useState } from "react";
import { Button, notification } from "antd";
import { CheckOutlined, CloseOutlined, RightOutlined, TeamOutlined } from "@ant-design/icons";
import { formatEnrolleeName, isActiveStatus } from "../search/searchResultUtils";
import { EnroleeBiodataModal } from "../compoinents/enroleeData";
import { PolicyHolderModal } from "../compoinents/userData";
import { myRequestObj } from "../../assets/utils/lib";
import { ADMIN_REVIEW_MODAL_CLASS } from "../dashboard/utils";

function Fact({ children }) {
  if (children == null || children === "") return null;
  return <span className="listing-card__fact">{children}</span>;
}

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

/**
 * Compact linear enrollee card for the verification queue.
 */
export function EnrolleeListingCard({ record, actions, onClick }) {
  const active = record?.status != null ? isActiveStatus(record.status) : null;
  const location = [record?.city, record?.state].filter(Boolean).join(", ");

  return (
    <article
      className="listing-card listing-card--linear search-result-card"
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="listing-card__lead">
        <div className="listing-card__icon is-enrollee">
          <TeamOutlined />
        </div>
        <div className="listing-card__identity">
          <h3 className="listing-card__title">{formatEnrolleeName(record)}</h3>
          <p className="listing-card__meta">{record?.insurance_no || record?.policy_no || "No ID"}</p>
        </div>
      </div>

      <div className="listing-card__facts">
        <Fact>{record?.email}</Fact>
        <Fact>{record?.phone}</Fact>
        <Fact>{record?.policy_no ? `Policy ${record.policy_no}` : null}</Fact>
        <Fact>{location}</Fact>
        <Fact>{formatDate(record?.date) ? `Registered ${formatDate(record.date)}` : null}</Fact>
      </div>

      <div className="listing-card__aside" onClick={(e) => e.stopPropagation()}>
        {active != null ? (
          <span className={`listing-card__status ${active ? "is-active" : "is-inactive"}`}>
            {active ? <CheckOutlined /> : <CloseOutlined />}
            {active ? "Active" : "Inactive"}
          </span>
        ) : null}
        {actions}
        {onClick ? <RightOutlined className="listing-card__chevron" aria-hidden /> : null}
      </div>
    </article>
  );
}

export function VerifyEnrolleeCard({ data }) {
  const [showModal, setShowModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyData, setPolicyData] = useState(null);

  const handleViewPolicy = async (event) => {
    event.stopPropagation();
    const policyNo = data?.policy_no;

    if (!policyNo) {
      notification.warning({
        message: "Policy number not available for this enrollee",
        placement: "topLeft",
      });
      return;
    }

    setPolicyLoading(true);
    try {
      const response = await myRequestObj("searchClients").post({ search: policyNo });
      const payload = response?.data;

      if (Number(payload?.error) !== 0) {
        notification.error({
          message: payload?.message || "Unable to load policy details",
          placement: "topLeft",
        });
        return;
      }

      const matchedPolicy = Array.isArray(payload?.data)
        ? payload.data.find((policy) => policy?.policy_no === policyNo) || payload.data[0]
        : null;

      if (!matchedPolicy) {
        notification.warning({
          message: "No matching policy was found for this enrollee",
          placement: "topLeft",
        });
        return;
      }

      setPolicyData(matchedPolicy);
      setShowPolicyModal(true);
    } catch (error) {
      notification.error({
        message: error?.message || "Failed to fetch policy details",
        placement: "topLeft",
      });
    } finally {
      setPolicyLoading(false);
    }
  };

  return (
    <>
      <EnrolleeListingCard
        record={data}
        onClick={() => setShowModal(true)}
        actions={
          <>
            <Button type="link" size="small" className="listing-card__action" onClick={() => setShowModal(true)}>
              Review
            </Button>
            <Button
              type="link"
              size="small"
              className="listing-card__action"
              loading={policyLoading}
              onClick={handleViewPolicy}
            >
              Policy
            </Button>
          </>
        }
      />
      {showModal ? (
        <EnroleeBiodataModal
          data={data}
          display={showModal}
          action={() => setShowModal(false)}
          modalClassName={ADMIN_REVIEW_MODAL_CLASS}
        />
      ) : null}
      {showPolicyModal ? (
        <PolicyHolderModal
          data={policyData || { policy_no: data?.policy_no }}
          display={showPolicyModal}
          action={() => setShowPolicyModal(false)}
          onclose={() => setShowPolicyModal(false)}
          showAllActions
          modalClassName={ADMIN_REVIEW_MODAL_CLASS}
        />
      ) : null}
    </>
  );
}
